import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import ContentCopyIcon from '@material-ui/icons/ContentCopy';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import './styles/projectInfoPopover.css';
import { useStyles } from '../styles';

type CopyField = 'projectId' | 'transactionId';

interface ProjectInfoPopoverProps {
  projectId: string;
  transactionId?: string;
  className?: string;
  buttonSize?: 'small' | 'medium';
}

const COPY_CONFIRM_DURATION_MS = 1500;

const ProjectInfoPopover = ({
  projectId,
  transactionId,
  className,
  buttonSize = 'small',
}: ProjectInfoPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [copiedField, setCopiedField] = useState<CopyField | null>(null);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const classes = useStyles();

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopy = async (
    event: React.MouseEvent<HTMLButtonElement>,
    field: CopyField,
    value?: string,
  ) => {
    event.stopPropagation();

    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
      resetTimerRef.current = setTimeout(() => {
        setCopiedField(null);
      }, COPY_CONFIRM_DURATION_MS);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? `project-info-popover-${projectId}` : undefined;

  const renderDetail = (
    label: string,
    value: string | undefined,
    field: CopyField,
  ) => {
    const hasValue = Boolean(value);
    const isCopied = copiedField === field;

    return (
      <Box className="project-info-popover-detail-block">
        <Typography
          component="span"
          className="project-info-popover-detail-label"
        >
          {label}
        </Typography>
        <Box className="project-info-popover-value-row">
          <Typography
            component="span"
            className="project-info-popover-detail-value"
          >
            {hasValue ? value : '—'}
          </Typography>
          <Tooltip title={isCopied ? 'Copied!' : `Copy ${label}`}>
            <span>
              <IconButton
                aria-label={isCopied ? `${label} copied` : `Copy ${label}`}
                className="project-info-popover-copy-btn"
                disabled={!hasValue}
                onClick={event => handleCopy(event, field, value)}
                size="small"
              >
                {isCopied ? (
                  <CheckIcon
                    className="project-info-popover-copied-icon"
                    fontSize="small"
                  />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Tooltip title="Project details">
        <IconButton
          aria-describedby={popoverId}
          aria-label="Project details"
          className={`project-info-popover-trigger${
            className ? ` ${className}` : ''
          }`}
          color="primary"
          onClick={handleOpen}
          size={buttonSize}
        >
          <InfoOutlinedIcon fontSize={buttonSize} />
        </IconButton>
      </Tooltip>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{ className: classes.projectInfoPopoverPaper }}
      >
        <Box
          className="project-info-popover-content"
          onClick={event => event.stopPropagation()}
        >
          <Typography variant="caption" className="project-info-popover-header">
            Project Details
          </Typography>
          {renderDetail('Project ID', projectId, 'projectId')}
          {renderDetail('Transaction ID', transactionId, 'transactionId')}
        </Box>
      </Popover>
    </>
  );
};

export default ProjectInfoPopover;
